import { useEffect } from 'react';
import { useCookieConsent } from './useCookieConsent';

declare global {
  interface Window {
    dataLayer: any[];
  }
}

export const useGTM = () => {
  const { consent } = useCookieConsent();

  useEffect(() => {
    const GTM_ID = import.meta.env.VITE_GTM_CONTAINER_ID;
    
    if (!GTM_ID || GTM_ID === 'GTM-XXXXXXX' || typeof window === 'undefined') {
      return;
    }

    // Only initialize GTM if analytics consent is given
    if (consent.analytics && !document.querySelector(`script[src*="googletagmanager.com/gtm.js"]`)) {
      // Defer GTM loading until after page is interactive
      const loadGTM = () => {
        // Initialize dataLayer
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          'gtm.start': new Date().getTime(),
          event: 'gtm.js'
        });

        // Load GTM script
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
        document.head.appendChild(script);

        // Add noscript iframe
        const noscript = document.createElement('noscript');
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.googletagmanager.com/ns.html?id=${GTM_ID}`;
        iframe.height = '0';
        iframe.width = '0';
        iframe.style.display = 'none';
        iframe.style.visibility = 'hidden';
        noscript.appendChild(iframe);
        document.body.insertBefore(noscript, document.body.firstChild);
      };

      // Defer loading using requestIdleCallback or setTimeout
      if ('requestIdleCallback' in window) {
        requestIdleCallback(loadGTM);
      } else {
        setTimeout(loadGTM, 2000);
      }
    }
  }, [consent.analytics]);

  const pushToDataLayer = (data: Record<string, any>) => {
    if (!consent.analytics || typeof window === 'undefined') {
      return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
  };

  return { pushToDataLayer };
};
